import {
  collection,
  doc,
  getDocs,
  getFirestore,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import type { AdminUsersContextValue } from "../../../context/types";
import {
  useAdminUsersActions,
} from "../../../context/AppContext";
import type {
  ModalActualizarPagoProps,
  SelectedAdmin,
  UseModalActualizarPagoReturn,
} from "./types";

const obtenerProximoPago = (fechaBase: Date): Date => {
  const proximoPago = new Date(fechaBase);
  proximoPago.setMonth(proximoPago.getMonth() + 1);

  return proximoPago;
};

const actualizarRutasAdmin = async (
  adminUid: string,
  proximoPago: Date
): Promise<void> => {
  const db = getFirestore();
  const querySnapshot = await getDocs(collection(db, "admin_users", adminUid, "rutas"));

  if (querySnapshot.empty) {
    return;
  }

  const batch = writeBatch(db);

  querySnapshot.docs.forEach((rutaDoc) => {
    batch.update(rutaDoc.ref, { proximoPago });
  });

  await batch.commit();
};

const actualizarAdminEnContexto = (
  selectedAdmin: SelectedAdmin,
  ultimoPago: Date,
  proximoPago: Date,
  setUsersAdminData: AdminUsersContextValue["setUsersAdminData"]
): void => {
  setUsersAdminData((prevAdmin) =>
    prevAdmin
      ? prevAdmin.map((admin) =>
          admin.uid === selectedAdmin.uid
            ? { ...admin, ultimoPago, proximoPago }
            : admin
        )
      : prevAdmin
  );
};

export const useModalActualizarPago = ({
  selectedAdmin,
  setIsActualizarPago,
}: ModalActualizarPagoProps): UseModalActualizarPagoReturn | null => {
  const [isEditing, setIsEditing] = useState(false);
  const { setUsersAdminData } = useAdminUsersActions();

  const handleClose = useCallback(() => {
    setIsActualizarPago(false);
  }, [setIsActualizarPago]);

  const actualizarPago = useCallback(async () => {
    if (!selectedAdmin || isEditing) {
      return;
    }

    setIsEditing(true);

    try {
      const db = getFirestore();
      const adminRef = doc(db, "admin_users", selectedAdmin.uid);
      const ultimoPago = new Date();
      const proximoPago = obtenerProximoPago(ultimoPago);

      await updateDoc(adminRef, {
        ultimoPago,
        proximoPago,
      });

      actualizarAdminEnContexto(
        selectedAdmin,
        ultimoPago,
        proximoPago,
        setUsersAdminData
      );
      await actualizarRutasAdmin(selectedAdmin.uid, proximoPago);

      toast.success("Pago Actualizado");
      setIsActualizarPago(false);
    } catch (error) {
      console.error("Error al actualizar pago", error);
    } finally {
      setIsEditing(false);
    }
  }, [isEditing, selectedAdmin, setIsActualizarPago, setUsersAdminData]);

  if (!selectedAdmin) {
    return null;
  }

  return {
    actualizarPago,
    handleClose,
    isEditing,
    selectedAdmin,
  };
};
